if (!window.animatedMedia) {
  class AnimatedMedia {
    constructor() {
      this.states = new WeakMap();
      this.loopCache = new Map();
      this.timingCache = new Map();

      document.querySelectorAll('img').forEach((img) => this.watch(img));

      new MutationObserver((records) => {
        records.forEach((record) => {
          if (record.type === 'attributes') {
            this.watch(record.target);
            return;
          }
          record.addedNodes.forEach((node) => {
            if (node.nodeType !== 1) return;
            if (node.tagName === 'IMG') {
              this.watch(node);
            } else {
              node.querySelectorAll('img').forEach((img) => this.watch(img));
            }
          });
        });
      }).observe(document.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['src', 'srcset']
      });
    }

    static sourceOf(img) {
      return { src: img.getAttribute('src') || '', srcset: img.getAttribute('srcset') || '' };
    }

    static isGif({ src, srcset }) {
      return /\.gif(\?|$|\s)/i.test(`${src} ${srcset}`);
    }

    static isMainImage(img) {
      return Boolean(img.closest('.media-viewer__item .media')) && !img.classList.contains('js-zoom-image');
    }

    watch(img) {
      if (img.tagName !== 'IMG') return;

      const source = AnimatedMedia.sourceOf(img);
      const state = this.states.get(img);
      if (state) {
        if (source.src === state.still) return;
        if (source.src === state.original.src && source.srcset === state.original.srcset) return;
        this.forget(img);
      }

      if (!AnimatedMedia.isGif(source)) return;

      const token = {};
      this.states.set(img, { original: source, still: null, token });

      if (img.complete && img.naturalWidth) {
        this.check(img, token);
      } else {
        img.addEventListener('load', () => this.check(img, token), { once: true });
      }
    }

    forget(img) {
      const state = this.states.get(img);
      if (!state) return;
      clearTimeout(state.autoStop);
      if (state.still) URL.revokeObjectURL(state.still);
      if (state.button) state.button.remove();
      this.states.delete(img);
    }

    isLooping(url) {
      if (!this.loopCache.has(url)) {
        const marker = [78, 69, 84, 83, 67, 65, 80, 69, 50, 46, 48];
        const request = fetch(url, { headers: { Accept: 'image/gif', Range: 'bytes=0-8191' } })
          .then((response) => response.arrayBuffer())
          .then((buffer) => {
            const bytes = new Uint8Array(buffer, 0, Math.min(buffer.byteLength, 8192));
            for (let i = 0; i <= bytes.length - marker.length; i += 1) {
              if (marker.every((byte, j) => bytes[i + j] === byte)) return true;
            }
            return false;
          })
          .catch(() => false);
        this.loopCache.set(url, request);
      }
      return this.loopCache.get(url);
    }

    static readTiming(buffer) {
      const bytes = new Uint8Array(buffer);
      const skipSubBlocks = (start) => {
        let p = start;
        while (p < bytes.length && bytes[p] !== 0) p += bytes[p] + 1;
        return p + 1;
      };
      const tableSize = (flags) => ((flags & 0x80) ? 3 * (2 ** ((flags & 7) + 1)) : 0);

      if (String.fromCharCode(...bytes.slice(0, 3)) !== 'GIF') return null;

      let p = 13 + tableSize(bytes[10]);
      let duration = 0;
      let shortestFrame = Infinity;
      let pendingDelay = 0;

      while (p < bytes.length) {
        const block = bytes[p];
        p += 1;
        if (block === 0x3b) break;
        if (block === 0x21) {
          if (bytes[p] === 0xf9) pendingDelay = bytes[p + 3] + (bytes[p + 4] << 8);
          p = skipSubBlocks(p + 1);
        } else if (block === 0x2c) {
          const frame = (pendingDelay <= 1 ? 10 : pendingDelay) * 10;
          duration += frame;
          shortestFrame = Math.min(shortestFrame, frame);
          pendingDelay = 0;
          p = skipSubBlocks(p + 10 + tableSize(bytes[p + 8]));
        } else {
          break;
        }
      }

      return duration ? { duration, flashes: shortestFrame < 1000 / 6 } : null;
    }

    timing(url) {
      if (!this.timingCache.has(url)) {
        const request = fetch(url, { headers: { Accept: 'image/gif' } })
          .then((response) => response.arrayBuffer())
          .then((buffer) => AnimatedMedia.readTiming(buffer))
          .catch(() => null);
        this.timingCache.set(url, request);
      }
      return this.timingCache.get(url);
    }

    async check(img, token) {
      const state = this.states.get(img);
      const url = img.currentSrc || img.src;
      if (!state || state.token !== token || !url || !(await this.isLooping(url))) return;
      if (this.states.get(img) !== state) return;

      if (!AnimatedMedia.isMainImage(img)) {
        this.pause(img);
        return;
      }

      if (!(await this.pause(img))) return;

      const button = this.addToggle(img);
      if (!button || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const timing = await this.timing(url);
      if (!timing || timing.flashes || this.states.get(img) !== state || state.touched) return;

      this.play(img);
      button.classList.remove('is-paused');
      state.autoStop = setTimeout(async () => {
        if (!state.still && await this.pause(img)) button.classList.add('is-paused');
      }, timing.duration * 3);
    }

    pause(img) {
      const state = this.states.get(img);
      return new Promise((resolve) => {
        if (!state) {
          resolve(false);
          return;
        }

        const ratio = img.naturalHeight / img.naturalWidth || 1;
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(img.naturalWidth, Math.round(img.clientWidth * (window.devicePixelRatio || 1)));
        canvas.height = Math.round(canvas.width * ratio);

        try {
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => {
            if (!blob || this.states.get(img) !== state) {
              resolve(false);
              return;
            }
            if (state.still) URL.revokeObjectURL(state.still);
            state.still = URL.createObjectURL(blob);
            if (!state.sources) {
              const picture = img.parentElement.tagName === 'PICTURE' ? img.parentElement : null;
              state.sources = picture
                ? [...picture.querySelectorAll('source[srcset]')].map((source) => ({ source, srcset: source.getAttribute('srcset') }))
                : [];
            }
            state.sources.forEach(({ source }) => source.removeAttribute('srcset'));
            img.removeAttribute('srcset');
            img.src = state.still;
            resolve(true);
          });
        } catch {
          resolve(false);
        }
      });
    }

    play(img) {
      const state = this.states.get(img);
      if (!state) return;
      if (state.sources) state.sources.forEach(({ source, srcset }) => source.setAttribute('srcset', srcset));
      if (state.original.srcset) img.setAttribute('srcset', state.original.srcset);
      img.src = state.original.src;
      if (state.still) URL.revokeObjectURL(state.still);
      state.still = null;
    }

    addToggle(img) {
      const container = img.closest('.media');
      const template = document.querySelector('template[data-animated-media-toggle]');
      if (!container || !template || container.querySelector('.animated-media__toggle')) return null;

      const state = this.states.get(img);
      const button = template.content.firstElementChild.cloneNode(true);
      button.classList.toggle('is-paused', Boolean(state.still));
      button.addEventListener('click', async (event) => {
        event.preventDefault();
        event.stopPropagation();
        clearTimeout(state.autoStop);
        state.touched = true;

        if (state.still) {
          this.play(img);
          button.classList.remove('is-paused');
        } else if (await this.pause(img)) {
          button.classList.add('is-paused');
        }
      });
      container.append(button);
      state.button = button;
      return button;
    }
  }

  window.animatedMedia = new AnimatedMedia();
}
